import React, { useState } from 'react';
import './SendingPlanPanel.css';

const TIER_COLORS = {
  deliverable: '#16a34a',
  unverified: '#b9b6c9',
  risky: '#d97706',
  rejected: '#dc2626',
};

const TIER_LABELS = {
  deliverable: 'Deliverable',
  unverified: 'Unverified',
  risky: 'Risky',
  rejected: 'Rejected',
};

function formatDay(isoDate, dayOffset) {
  const d = new Date(`${isoDate}T00:00:00`);
  const label = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  if (dayOffset === 0) return `Today (${label})`;
  if (dayOffset === 1) return `Tomorrow (${label})`;
  if (dayOffset < 0) return `Waiting since ${label}`;
  return label;
}

function TierBar({ tiers, count }) {
  return (
    <div className="sp-tierbar">
      {Object.entries(tiers).map(([tier, n]) => (
        <div key={tier} title={`${TIER_LABELS[tier] || tier}: ${n}`}
             style={{ width: `${(n / count) * 100}%`, background: TIER_COLORS[tier] || '#b9b6c9' }} />
      ))}
    </div>
  );
}

function BuyerList({ buyers }) {
  if (!buyers.length) return null;
  return (
    <ul className="sp-buyers">
      {buyers.map((b, i) => (
        <li key={`${b.buyer_id}-${b.message_index}-${i}`} className="sp-buyer">
          <span className="sp-buyer-name">{b.business_name}</span>
          <span className="sp-buyer-tier" style={{ color: TIER_COLORS[b.tier] || '#b9b6c9' }}>
            {b.tier_label}{typeof b.score === 'number' ? ` ${b.score}` : ''}
          </span>
          <div className="sp-buyer-reason">{b.reason}</div>
        </li>
      ))}
    </ul>
  );
}

function SendingPlanPanel({ breakdown, onRefresh }) {
  const [expanded, setExpanded] = useState(true);
  const [openDay, setOpenDay] = useState(null);
  const [openSection, setOpenSection] = useState(null);

  if (!breakdown) return null;
  const { ramp, days, sent, follow_ups: followUps, excluded, simulated } = breakdown;
  const dncCount = breakdown.skipped_dnc?.count || 0;
  const rampStatus = ramp?.status;

  return (
    <div className="sp-panel">
      <div className="sp-head">
        <h2 className="sp-title" onClick={() => setExpanded(!expanded)}>
          Sending Plan {expanded ? '▲' : '▼'}
        </h2>
        <button onClick={onRefresh} className="sp-refresh">
          Refresh
        </button>
      </div>

      {expanded && (
        <div className="sp-body">
          {simulated && (
            <p className="sp-note sp-note--preview">
              Preview: this campaign has not launched yet. This is what launching now would do.
            </p>
          )}
          {dncCount > 0 && (
            <p className="sp-note sp-note--muted">
              {dncCount} skipped: marked Do Not Contact.
            </p>
          )}
          {ramp && rampStatus === 'no_sender' && (
            <p className="sp-note sp-note--danger">
              <strong>No sending address found.</strong> Set a Sender email in Settings. Without one the ramp can't work and every email would send today.
            </p>
          )}
          {ramp && rampStatus !== 'no_sender' && (
            <p className="sp-note sp-note--ramp">
              {rampStatus === 'no_active_ramp'
                ? `Ramp is on for this campaign, but no ramp is active for ${ramp.from_email || 'its sending address'} yet. It starts when the campaign launches.`
                : ramp.cap_today !== null && ramp.cap_today !== undefined
                  ? `Ramp on${ramp.from_email ? ` for ${ramp.from_email}` : ''}: day ${ramp.day}, up to ${ramp.cap_today} emails a day.`
                  : ramp.day !== null && ramp.day !== undefined
                    ? `Ramp on${ramp.from_email ? ` for ${ramp.from_email}` : ''}: ramp complete, no daily cap.`
                    : `Ramp is on for this campaign, but no ramp is active for ${ramp.from_email || 'its sending address'} yet. It starts when the campaign launches.`}
            </p>
          )}

          {days.length === 0 ? (
            <p className="sp-empty">Nothing is waiting to send as a first message.</p>
          ) : (
            days.map((d) => (
              <div key={d.date} className="sp-row">
                <div className="sp-day"
                     onClick={() => setOpenDay(openDay === d.date ? null : d.date)}>
                  <strong className="sp-day-label">{formatDay(d.date, d.day_offset)}</strong>
                  <span className="sp-day-count">
                    {d.count} email{d.count === 1 ? '' : 's'}{d.cap !== null && d.cap !== undefined ? ` (cap ${d.cap})` : ''}
                  </span>
                  <TierBar tiers={d.tiers} count={d.count} />
                  <span className="sp-day-tiers">
                    {Object.entries(d.tiers).map(([t, n]) => `${n} ${(TIER_LABELS[t] || t).toLowerCase()}`).join(' · ')}
                  </span>
                  <span className="sp-caret">{openDay === d.date ? '▲' : '▼'}</span>
                </div>
                {openDay === d.date && (
                  <>
                    <BuyerList buyers={d.buyers} />
                    {d.count > d.buyers.length && (
                      <p className="sp-more">Showing first {d.buyers.length} of {d.count}.</p>
                    )}
                  </>
                )}
              </div>
            ))
          )}

          {[['sent', 'Already sent', sent], ['followups', 'Follow-ups waiting', followUps]].map(([key, title, section]) => (
            section.count > 0 && (
              <div key={key} className="sp-row">
                <div className="sp-section" onClick={() => setOpenSection(openSection === key ? null : key)}>
                  <strong>{title}</strong> ({section.count}) {openSection === key ? '▲' : '▼'}
                </div>
                {openSection === key && <BuyerList buyers={section.buyers} />}
              </div>
            )
          ))}

          {excluded.count > 0 && (
            <p className="sp-excluded">
              {excluded.count} excluded because Hunter marked their address as rejected. See the Excluded section below.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default SendingPlanPanel;
